import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    FormHelperText,
    Checkbox,
    FormGroup,
    TextField,
    Button,
    Alert,
    Divider,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Select,
    MenuItem,
    InputLabel,
    CircularProgress,
    Tooltip, // Added
} from '@mui/material';
import {
    Info as InfoIcon,
    ExpandMore as ExpandMoreIcon,
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
    InsertDriveFile as FileIcon,
    Gavel as GavelIcon,
    Engineering as EngineeringIcon,
    RequestQuote as QuoteIcon,
    ThumbUpAlt as ThumbUpIcon, // Added
    ThumbUpOffAlt as ThumbUpOutlinedIcon, // Added
    ThumbDownAlt as ThumbDownIcon, // Added
    ThumbDownOffAlt as ThumbDownOutlinedIcon, // Added
} from '@mui/icons-material';
import { LocalDocument } from '../ProjectCreation/DocumentsStep';

interface QuestionOption {
    value: string;
    label: string;
    description?: string;
}

interface QuestionValidation {
    min?: number;
    max?: number;
    unit?: string;
}

interface QuestionDependency {
    target_question_id: string;
    trigger_values: (string | 'ANY')[];
}

interface Question {
    question_id: string;
    question_text: string;
    question_type: 'single-select' | 'multi-select' | 'number' | 'text' | 'yes-no';
    required: boolean;
    help_text?: string;
    options?: QuestionOption[];
    validation?: QuestionValidation;
    scope_impact?: string;
    dependency?: QuestionDependency;
}

interface RegulatoryDefault {
    element: string;
    assumption: string;
    regulation: string;
    reason: string;
}

interface EngineerClarification {
    element: string;
    current_working_assumption: string;
    question_for_engineer: string;
}

interface ScopeAssumptions {
    element: string;
    base_assumption: string;
    variation_trigger: string;
    confidence_level: 'High' | 'Medium' | 'Low';
}

interface TechnicalSpecifications {
    regulatory_defaults?: RegulatoryDefault[];
    engineer_clarifications?: EngineerClarification[];
    scope_assumptions?: ScopeAssumptions[];
}

interface PhaseQuestionsProps {
    phaseName: string;
    questions: Question[];
    technicalSpecifications?: TechnicalSpecifications;
    estimatedCompletionTime?: string;
    onSubmit: (answers: Record<string, any>) => void;
    onCancel?: () => void;
    initialAnswers?: Record<string, any>;
    onQuestionFeedback?: (questionId: string, rating: 'up' | 'down') => void; // Added prop
}

const PhaseQuestions: React.FC<PhaseQuestionsProps> = ({
    phaseName,
    questions,
    technicalSpecifications = {},
    estimatedCompletionTime,
    onSubmit,
    onCancel,
    initialAnswers = {},
    onQuestionFeedback,
}) => {
    const [answers, setAnswers] = useState<Record<string, any>>(initialAnswers);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [additionalRequirements, setAdditionalRequirements] = useState<string>(initialAnswers['additional_requirements'] || '');
    const [phaseDocuments, setPhaseDocuments] = useState<LocalDocument[]>(initialAnswers['phase_documents'] || []);
    const [selectedDocType, setSelectedDocType] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string>('');
    
    // State for feedback icons
    const [feedbackState, setFeedbackState] = useState<Record<string, 'up' | 'down'>>({});

    useEffect(() => {
        setAnswers(initialAnswers);
        setIsSubmitted(Object.keys(initialAnswers).length > 0);
    }, [initialAnswers]);

    const hasTechSpecs =
        (technicalSpecifications.regulatory_defaults?.length || 0) > 0 ||
        (technicalSpecifications.engineer_clarifications?.length || 0) > 0 ||
        (technicalSpecifications.scope_assumptions?.length || 0) > 0;


    const shouldShowQuestion = (question: Question): boolean => {
        if (!question.dependency) return true;
        if (!question.dependency?.target_question_id) return true;
        const targetValue = answers[question.dependency.target_question_id];
        const triggerValues = question.dependency.trigger_values;

        if (triggerValues.includes('ANY')) {
            return targetValue !== undefined && targetValue !== '' && targetValue !== null;
        }
        return triggerValues.includes(String(targetValue));
    };

    const visibleQuestions = questions.filter(shouldShowQuestion);

    const handleFeedback = (questionId: string, rating: 'up' | 'down') => {
        setFeedbackState(prev => ({
            ...prev,
            [questionId]: rating
        }));
        if (onQuestionFeedback) {
            onQuestionFeedback(questionId, rating);
        }
    };

    const handleSingleSelectChange = (questionId: string, value: string) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
        setErrors(prev => ({ ...prev, [questionId]: '' }));
    };

    const handleMultiSelectChange = (questionId: string, value: string, checked: boolean) => {
        setAnswers(prev => {
            const currentValues = prev[questionId] || [];
            if (checked) {
                return { ...prev, [questionId]: [...currentValues, value] };
            } else {
                return { ...prev, [questionId]: currentValues.filter((v: string) => v !== value) };
            }
        });
        setErrors(prev => ({ ...prev, [questionId]: '' }));
    };

    const handleTextChange = (questionId: string, value: string) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
        setErrors(prev => ({ ...prev, [questionId]: '' }));
    };

    const handleNumberChange = (questionId: string, value: string) => {
        const numValue = value === '' ? '' : parseFloat(value);
        setAnswers(prev => ({ ...prev, [questionId]: numValue }));
        setErrors(prev => ({ ...prev, [questionId]: '' }));
    };

    const validateAnswers = (): boolean => {
        const newErrors: Record<string, string> = {};
        let isValid = true;

        visibleQuestions.forEach(question => {
            if (question.required && !answers[question.question_id]) {
                newErrors[question.question_id] = 'This question is required';
                isValid = false;
            }
            if (question.question_type === 'number' && answers[question.question_id]) {
                const value = parseFloat(answers[question.question_id]);
                if (question.validation?.min !== undefined && value < question.validation.min) {
                    newErrors[question.question_id] = `Value must be at least ${question.validation.min}`;
                    isValid = false;
                }
                if (question.validation?.max !== undefined && value > question.validation.max) {
                    newErrors[question.question_id] = `Value must be at most ${question.validation.max}`;
                    isValid = false;
                }
            }
        });
        setErrors(newErrors);
        return isValid;
    };

    // Helper to capitalize the first letter
    const toSentenceCase = (text: string) => {
        if (!text) return '';
        const trimmed = text.trim();
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    };

    const handleSubmit = () => {
        if (validateAnswers()) {
            setIsSubmitted(true);
            
            const finalAnswers = { ...answers };

            // 1. Handle "Other" text formatting (Existing Logic)
            visibleQuestions.forEach(q => {
                const customKey = `${q.question_id}_custom`;
                if (q.question_type === 'single-select' || q.question_type === 'yes-no') {
                    if (finalAnswers[q.question_id] === 'other') {
                        finalAnswers[q.question_id] = toSentenceCase(finalAnswers[customKey]);
                    }
                } 
                else if (q.question_type === 'multi-select') {
                    if (Array.isArray(finalAnswers[q.question_id])) {
                        const selectedOptions = finalAnswers[q.question_id] as string[];
                        if (selectedOptions.includes('other')) {
                            const newOptions = selectedOptions.filter(opt => opt !== 'other');
                            if (finalAnswers[customKey]) {
                                newOptions.push(toSentenceCase(finalAnswers[customKey]));
                            }
                            finalAnswers[q.question_id] = newOptions;
                        }
                    }
                }
                delete finalAnswers[customKey];
            });

            // 2. Filter to only include active questions (Existing Logic)
            const filteredAnswers = Object.keys(finalAnswers)
                .filter(id => questions.find(q => q.question_id === id && shouldShowQuestion(q)))
                .reduce((obj, key) => {
                    obj[key] = finalAnswers[key];
                    return obj;
                }, {} as Record<string, any>);

            // 3. Add Standard Extras (Existing Logic)
            if (additionalRequirements) filteredAnswers['additional_requirements'] = additionalRequirements;
            if (phaseDocuments.length > 0) filteredAnswers['phase_documents'] = phaseDocuments;

            // --- NEW CODE START ---
            // 4. Inject the Feedback Data
            // This saves the feedback map (e.g. { "q1": "up", "q2": "down" }) 
            // into the same JSON blob as the answers.
            if (Object.keys(feedbackState).length > 0) {
                filteredAnswers['_question_feedback'] = feedbackState;
            }
            // --- NEW CODE END ---

            onSubmit(filteredAnswers);
        }
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0 || !selectedDocType) return;
        const newDocs: LocalDocument[] = files.map(file => ({
            file, documentType: selectedDocType, id: Math.random().toString(36).substr(2, 9)
        }));
        setPhaseDocuments(prev => [...prev, ...newDocs]);
        setSelectedDocType('');
        event.target.value = '';
    };

    const removePhaseDocument = (documentId: string) => {
        setPhaseDocuments(prev => prev.filter(doc => doc.id !== documentId));
    };

    const getFileIcon = (fileName: string) => {
        const ext = fileName?.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf': return <PdfIcon color="error" />;
            case 'jpg': case 'jpeg': case 'png': case 'gif': return <ImageIcon color="primary" />;
            default: return <FileIcon color="action" />;
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const renderQuestion = (question: Question) => {
        const hasError = !!errors[question.question_id];
        const answer = answers[question.question_id];
        
        const customTextKey = `${question.question_id}_custom`;
        const customTextValue = answers[customTextKey] || '';

        const prepareOptions = (rawOptions: QuestionOption[] = []) => {
            let options = [...rawOptions];
            
            if (answer) {
                if (question.question_type === 'single-select' || question.question_type === 'yes-no') {
                    const match = options.find(opt => opt.value === answer);
                    if (!match && answer !== 'other') {
                        options.push({ value: answer, label: answer });
                    }
                } 
                else if (question.question_type === 'multi-select' && Array.isArray(answer)) {
                    answer.forEach((val: string) => {
                        const match = options.find(opt => opt.value === val);
                        if (!match && val !== 'other') {
                            options.push({ value: val, label: val });
                        }
                    });
                }
            }

            if (!isSubmitted || answer === 'other' || (Array.isArray(answer) && answer.includes('other'))) {
                 options.push({ 
                    value: 'other', 
                    label: 'Other (Please specify)', 
                    description: '' 
                });
            }

            return options;
        };

        switch (question.question_type) {
            case 'yes-no':
            case 'single-select': {
                let options: QuestionOption[] = [];
                if (question.question_type === 'yes-no') {
                    options = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];
                } else {
                    options = prepareOptions(question.options);
                }

                const isOtherSelected = answer === 'other';

                return (
                    <FormControl component="fieldset" error={hasError} fullWidth>
                        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>
                            {question.question_text}
                            {question.required && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.help_text && (
                            <FormHelperText sx={{ mt: 0, mb: 2 }}>
                                <InfoIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                {question.help_text}
                            </FormHelperText>
                        )}
                        <RadioGroup
                            value={answer || ''}
                            onChange={(e) => {
                                handleSingleSelectChange(question.question_id, e.target.value);
                                if (e.target.value !== 'other') {
                                    handleTextChange(customTextKey, '');
                                }
                            }}
                        >
                            {options.map((option) => (
                                <Box key={option.value} sx={{ mb: 1 }}>
                                    <FormControlLabel
                                        value={option.value}
                                        control={<Radio disabled={isSubmitted} />}
                                        label={
                                            <Box>
                                                <Typography variant="body1" color={isSubmitted ? 'text.secondary' : 'text.primary'}>
                                                    {option.label}
                                                </Typography>
                                                {option.description && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 0 }}>
                                                        {option.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                    {option.value === 'other' && isOtherSelected && (
                                        <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Please specify..."
                                                value={customTextValue}
                                                onChange={(e) => handleTextChange(customTextKey, e.target.value)}
                                                required={question.required && isOtherSelected}
                                                error={hasError && !customTextValue}
                                                disabled={isSubmitted}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </RadioGroup>
                        {hasError && <FormHelperText>{errors[question.question_id]}</FormHelperText>}
                        {question.scope_impact && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="caption">
                                    <strong>Scope Impact:</strong> {question.scope_impact}
                                </Typography>
                            </Alert>
                        )}
                    </FormControl>
                );
            }

            case 'multi-select': {
                const options = prepareOptions(question.options);
                const currentAnswers = (answer || []) as string[];
                const isOtherSelected = currentAnswers.includes('other');

                return (
                    <FormControl component="fieldset" error={hasError} fullWidth>
                        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>
                            {question.question_text}
                            {question.required && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.help_text && (
                            <FormHelperText sx={{ mt: 0, mb: 2 }}>
                                <InfoIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                {question.help_text}
                            </FormHelperText>
                        )}
                        <FormGroup>
                            {options.map((option) => (
                                <Box key={option.value} sx={{ mb: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                disabled={isSubmitted}
                                                checked={currentAnswers.includes(option.value)}
                                                onChange={(e) => {
                                                    handleMultiSelectChange(question.question_id, option.value, e.target.checked);
                                                    if (option.value === 'other' && !e.target.checked) {
                                                        handleTextChange(customTextKey, '');
                                                    }
                                                }}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body1" color={isSubmitted ? 'text.secondary' : 'text.primary'}>
                                                    {option.label}
                                                </Typography>
                                                {option.description && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 0 }}>
                                                        {option.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                    {option.value === 'other' && isOtherSelected && (
                                        <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Please specify..."
                                                value={customTextValue}
                                                onChange={(e) => handleTextChange(customTextKey, e.target.value)}
                                                required={question.required && isOtherSelected}
                                                disabled={isSubmitted}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </FormGroup>
                        {hasError && <FormHelperText>{errors[question.question_id]}</FormHelperText>}
                        {question.scope_impact && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="caption">
                                    <strong>Scope Impact:</strong> {question.scope_impact}
                                </Typography>
                            </Alert>
                        )}
                    </FormControl>
                );
            }

            case 'number':
                return (
                    <FormControl fullWidth error={hasError}>
                        <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            {question.question_text}
                            {question.required && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.help_text && (
                            <FormHelperText sx={{ mt: 0, mb: 2 }}>
                                <InfoIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                {question.help_text}
                            </FormHelperText>
                        )}
                        <TextField
                            type="number"
                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                            sx={{
                                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0,
                                },
                                '& input[type=number]': {
                                '-moz-appearance': 'textfield',
                                },
                            }}
                            value={answer || ''}
                            onChange={(e) => handleNumberChange(question.question_id, e.target.value)}
                            error={hasError}
                            helperText={hasError ? errors[question.question_id] : ''}
                            disabled={isSubmitted}
                            InputProps={{
                                endAdornment: question.validation?.unit && (
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                        {question.validation.unit}
                                    </Typography>
                                ),
                            }}
                            inputProps={{
                                min: question.validation?.min,
                                max: question.validation?.max,
                            }}
                        />
                        {question.scope_impact && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="caption">
                                    <strong>Scope Impact:</strong> {question.scope_impact}
                                </Typography>
                            </Alert>
                        )}
                    </FormControl>
                );

            case 'text':
                return (
                    <FormControl fullWidth error={hasError}>
                        <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            {question.question_text}
                            {question.required && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.help_text && (
                            <FormHelperText sx={{ mt: 0, mb: 2 }}>
                                <InfoIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                {question.help_text}
                            </FormHelperText>
                        )}
                        <TextField
                            multiline
                            rows={3}
                            value={answer || ''}
                            onChange={(e) => handleTextChange(question.question_id, e.target.value)}
                            error={hasError}
                            helperText={hasError ? errors[question.question_id] : ''}
                            placeholder="Enter your answer here..."
                            disabled={isSubmitted}
                        />
                        {question.scope_impact && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="caption">
                                    <strong>Scope Impact:</strong> {question.scope_impact}
                                </Typography>
                            </Alert>
                        )}
                    </FormControl>
                );

            default:
                return null;
        }
    };

    const answeredCount = visibleQuestions.filter(q => answers[q.question_id]).length;
    const totalCount = visibleQuestions.length;
    const progress = totalCount > 0 ? (answeredCount / totalCount) * 100 : 100;

    return (
        <Box>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {phaseName} Questions
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Please answer the following questions to help us understand your project requirements.
                    </Typography>
                    {estimatedCompletionTime && (
                        <Chip label={`Estimated time: ${estimatedCompletionTime}`} size="small" color="primary" variant="outlined" />
                    )}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Progress: {answeredCount} of {totalCount} active questions answered
                        </Typography>
                        <Box sx={{ width: '100%', height: 8, bgcolor: 'grey.200', borderRadius: 1, mt: 1, overflow: 'hidden' }}>
                            <Box sx={{ width: `${progress}%`, height: '100%', bgcolor: 'primary.main', transition: 'width 0.3s ease' }} />
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {hasTechSpecs && (
                <Accordion sx={{ mb: 3, bgcolor: '#f5f5f5' }} defaultExpanded={false}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <InfoIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            Technical Specifications & Assumptions
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {technicalSpecifications.regulatory_defaults && technicalSpecifications.regulatory_defaults.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <GavelIcon fontSize="small" sx={{ mr: 1 }} /> Regulatory Defaults (Safe Specs)
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                    {technicalSpecifications.regulatory_defaults.map((item, idx) => (
                                        <Box component="li" key={idx} sx={{ mb: 2 }}>
                                            <Typography variant="body2"><strong>{item.element}:</strong> {item.assumption}</Typography>
                                            <Typography variant="caption" display="block" color="text.secondary">Regulation: {item.regulation}</Typography>
                                            <Typography variant="caption" display="block" color="text.secondary">Why: {item.reason}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        {technicalSpecifications.engineer_clarifications && technicalSpecifications.engineer_clarifications.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: 'warning.dark', display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <EngineeringIcon fontSize="small" sx={{ mr: 1 }} /> Engineering Clarifications
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                    {technicalSpecifications.engineer_clarifications.map((item, idx) => (
                                        <Box component="li" key={idx} sx={{ mb: 2 }}>
                                            <Typography variant="body2"><strong>{item.element}:</strong> {item.current_working_assumption}</Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>Question for Engineer: "{item.question_for_engineer}"</Typography>
                                            <Typography variant="caption" display="block" color="error.main" sx={{ mt: 0.5 }}></Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        {technicalSpecifications.scope_assumptions && technicalSpecifications.scope_assumptions.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: 'success.dark', display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <QuoteIcon fontSize="small" sx={{ mr: 1 }} /> Scope Assumptions (For current quotations)
                                </Typography>
                                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                    {technicalSpecifications.scope_assumptions.map((item, idx) => (
                                        <Box component="li" key={idx} sx={{ mb: 2 }}>
                                            <Typography variant="body2"><strong>{item.element}:</strong> {item.base_assumption}</Typography>
                                            <Typography variant="body2">{item.variation_trigger}</Typography>
                                            <Chip
                                                label={`Risk: ${item.confidence_level}`}
                                                size="small"
                                                color={item.confidence_level.includes('HIGH') ? 'error' : item.confidence_level.includes('MEDIUM') ? 'warning' : 'success'}
                                                variant="outlined"
                                                sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}

            {/* NEW CODE START: Handle case where AI generated no questions */}
            {visibleQuestions.length === 0 && (
                <Card sx={{ mb: 3, border: '1px solid #4caf50' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <ThumbUpIcon color="success" sx={{ mr: 1 }} />
                            <Typography variant="h6" color="success.main">
                                No Questions Required
                            </Typography>
                        </Box>
                        <Typography variant="body1">
                            Based on your previous inputs and project constraints, the system has sufficient information for the <strong>{phaseName}</strong> phase.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Please review the <strong>Technical Specifications & Assumptions</strong> above to ensure our assumptions align with your expectations. If you have specific custom requirements, you can add them in the section below.
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {visibleQuestions.map((question, index) => (
                <Card key={question.question_id} sx={{ mb: 3 }}>
                    <CardContent>
                        {/* FEEDBACK CONTROLS - Only show if NOT submitted/loaded from DB */}
                        {!isSubmitted && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Question {index + 1} of {totalCount}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title="Your feedback on quality of questions is taken seriously and helps us improve the platform." arrow placement="top">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => handleFeedback(question.question_id, 'up')}
                                            color={feedbackState[question.question_id] === 'up' ? 'success' : 'default'}
                                        >
                                            {feedbackState[question.question_id] === 'up' ? (
                                                <ThumbUpIcon fontSize="small" />
                                            ) : (
                                                <ThumbUpOutlinedIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Your feedback on quality of questions is taken seriously and helps us improve the platform." arrow placement="top">
                                        <IconButton 
                                            size="small" 
                                            onClick={() => handleFeedback(question.question_id, 'down')}
                                            color={feedbackState[question.question_id] === 'down' ? 'error' : 'default'}
                                        >
                                            {feedbackState[question.question_id] === 'down' ? (
                                                <ThumbDownIcon fontSize="small" />
                                            ) : (
                                                <ThumbDownOutlinedIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        )}
                        {renderQuestion(question)}
                    </CardContent>
                </Card>
            ))}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Open Question 1
                    </Typography>
                    <FormControl fullWidth>
                        <FormLabel sx={{ mb: 1, fontWeight: 500 }}>
                            Are there any other specific requirements or constraints for {phaseName}?
                        </FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            value={additionalRequirements}
                            onChange={(e) => setAdditionalRequirements(e.target.value)}
                            placeholder="Enter any additional requirements..."
                            disabled={isSubmitted}
                        />
                    </FormControl>
                </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Open Question 2
                    </Typography>
                    <FormLabel sx={{ mb: 2, fontWeight: 500, display: 'block' }}>
                        Would you like to upload any additional files?
                    </FormLabel>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Document Type</InputLabel>
                                <Select 
                                    value={selectedDocType} 
                                    onChange={(e) => setSelectedDocType(e.target.value)} 
                                    label="Document Type"
                                    disabled={isSubmitted}
                                >
                                    {['Planning Application', 'Building Regulations', 'Structural Calculations', 'Architectural Drawings', 'Site Survey', 'Photos/Images', 'Other'].map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                                </Select>
                            </FormControl>
                            {uploadError && <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>}
                            <Button 
                                variant="outlined" 
                                component="label" 
                                startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />} 
                                fullWidth 
                                disabled={!selectedDocType || uploading || isSubmitted}
                            >
                                {uploading ? 'Uploading...' : 'Choose Files'}
                                <input type="file" hidden multiple accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.dwg,.dxf,.skp,.3dm,.rvt" onChange={handleFileUpload} />
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>Selected Files ({phaseDocuments.length})</Typography>
                            <List dense>
                                {phaseDocuments.map(doc => (
                                    <ListItem key={doc.id} sx={{ px: 0 }}>
                                        <ListItemIcon>{getFileIcon(doc.file.name)}</ListItemIcon>
                                        <ListItemText primary={doc.file.name} secondary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Chip label={doc.documentType} size="small" variant="outlined" /><Typography variant="caption" color="text.secondary">{formatFileSize(doc.file.size)}</Typography></Box>} />
                                        <IconButton 
                                            edge="end" 
                                            onClick={() => removePhaseDocument(doc.id)} 
                                            size="small"
                                            disabled={isSubmitted}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {answeredCount === totalCount && totalCount > 0 ? 'All questions answered! Ready to submit.' : `${totalCount - answeredCount} question${totalCount - answeredCount !== 1 ? 's' : ''} remaining`}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {onCancel && <Button variant="outlined" onClick={onCancel}>Cancel</Button>}
                            <Button 
                                variant="contained" 
                                onClick={handleSubmit} 
                                disabled={(totalCount > 0 && answeredCount < totalCount) || isSubmitted}>
                                {isSubmitted ? '✓ Answers Submitted' : visibleQuestions.length === 0 ? 'Confirm & Continue' : 'Submit Answers'}
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default PhaseQuestions;